/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-nested-ternary */
import React, { ReactElement, useContext, useState, useEffect } from 'react';
import ResultSeparator from '../common/ResultSeparator';
import ProgressRing from './ProgressRing';
import { LiveMatchContext } from '../../context';
import { ISportsPeriodScore } from '../../interfaces/interfaces';
import { getUTCGameTime, getUTCFromCET } from '../../helpers/helperFunctions';

type ScoreProps = {
  score: ISportsPeriodScore;
  gameStage: string;
  endWithPenalties: boolean;
};

const Score = ({ score, gameStage, endWithPenalties }: ScoreProps): ReactElement => {
  const { liveMatchData, sportType } = useContext(LiveMatchContext);

  const [minute, setMinute] = useState<number | undefined>(undefined);
  const [waiting, setWaiting] = useState<boolean>(true);

  useEffect(() => {
    const gameDateTimestamp = getUTCGameTime(liveMatchData.date);
    const currentTimestamp = getUTCFromCET();

    // If current time is less than game date, don't show timer
    if (currentTimestamp < gameDateTimestamp) {
      setWaiting(true);
      setMinute(undefined);
      return undefined;
    }

    setWaiting(false);

    // If gameStage is different then these values, dont show timer
    if (
      !['first-half', 'second-half-start', 'first-extra-start', 'second-extra-start'].includes(
        gameStage
      )
    ) {
      setMinute(undefined);
      return undefined;
    }

    // Get the matchEvent for the current gameStage
    const gameEvent = liveMatchData.details.matchEvent.find((event) => {
      const stage = gameStage === 'first-half' ? 'game-start' : gameStage;
      return event.matchAction === stage;
    });

    if (!gameEvent) {
      return undefined;
    }

    // Subtract the matchEvent time from the current time to get match minute
    const eventTimestamp = getUTCGameTime(gameEvent.created);

    const stageMinute = Math.floor((currentTimestamp - eventTimestamp) / 60000);

    setMinute(determineGameStageMinute(stageMinute, gameStage));

    return undefined;
  }, [liveMatchData, gameStage]);

  const bigScore = Number(score.currentScore.home) >= 10 || Number(score.currentScore.away) >= 10;
  const isFootball = sportType === 'football';

  return (
    <div className="widget-result-score-wrapper">
      <ProgressRing
        radius={62}
        stroke={4}
        progress={minute}
        gameStage={gameStage}
        sportType={sportType}
      />

      <div className="widget-result-score">
        <span
          className={`widget-result-score-home ${bigScore && 'widget-result-score-home-small'}`}>
          {waiting ? 0 : score.currentScore.home}
        </span>
        <ResultSeparator className={bigScore ? 'big-score' : ''} />
        <span
          className={`widget-result-score-away ${bigScore && 'widget-result-score-away-small'}`}>
          {waiting ? 0 : score.currentScore.away}
        </span>
      </div>
      {isFootball && minute !== undefined && (
        <div className="widget-result-score-minutes">
          {formatMinuteForDisplay(minute, gameStage)}’
        </div>
      )}
      {isFootball && endWithPenalties && (
        <div className="widget-result-penalty-score">
          <span>(</span> {score?.penaltyScore?.home} : {score.penaltyScore?.away} <span>)</span>
        </div>
      )}
    </div>
  );
};

export default Score;

// If match minute went over the regular time for that match stage, reset clock
function determineGameStageMinute(stageMinute: number, gameStage: string) {
  let minute;
  minute = stageMinute;
  if (gameStage === 'second-half-start') {
    minute = stageMinute + 45;
  }
  if (gameStage === 'first-extra-start') {
    minute = stageMinute + 90;
  }
  if (gameStage === 'second-extra-start') {
    minute = stageMinute + 105;
  }
  return minute;
}

// This function formats minute to display time correctly during injury time
function formatMinuteForDisplay(minute: number, gameStage: string): string {
  if (gameStage === 'first-half' && minute > 45) {
    return `45+${minute - 45}`;
  }
  if (gameStage === 'second-half-start' && minute > 90) {
    return `90+${minute - 90}`;
  }
  if (gameStage === 'first-extra-start') {
    return `105+${minute - 105}`;
  }
  if (gameStage === 'second-extra-start') {
    return `120+${minute - 120}`;
  }
  if (gameStage === 'first-half-end') {
    return '';
  }
  if (gameStage === 'second-half-end') {
    return '';
  }
  if (gameStage === 'first-extra-end') {
    return '';
  }
  if (gameStage === 'second-extra-end') {
    return '';
  }
  return minute.toString();
}
